import React, { useState, useEffect, useRef, useMemo } from "react";
import { Helmet } from "react-helmet";
import { useQuery } from "react-query";
import { useHistory, useParams } from "react-router";
import { Link } from "react-router-dom";

import { Fulfillment } from "@secondcloset/types";
import { ShipmentsTable, CheckboxDropdown, SearchOrganizationInput, SearchInput } from "@secondcloset/web-components";

import { FilterFilled, FilterOutlined } from "@ant-design/icons";
import { DatePicker, Select, Button, Empty, Typography, Space } from "antd";
import { debounce } from "lodash-es";
import moment, { Moment } from "moment";

import useUrlState from "hooks/application/useUrlState";

import { fetchOrganizationIndex } from "../../../api/accounts/organization";
import { fetchShipmentIndex, ShipmentIndexQuery } from "../../../api/logistics/shipment";
import { useOrganizationContext } from "../../../contextProviders/organization/OrganizationProvider";

import DownloadCsvButton from "./DownloadCsvButton";
import { Flex } from "../../../css/styles";

type DeliveryType = Fulfillment.DeliveryType;

enum ShipmentType {
  delivery = "Delivery",
  cross_dock_delivery = "Cross Dock Delivery",
  reverse_logistics = "Reverse Logistics",
  inventory_pick_up = "Inventory Pick Up",
  return_to_sender = "Return To Sender",
}

enum ShipmentMethod {
  external_carrier_shipment = "External Carrier Parcel",
  last_mile_heavy = "GoBolt Freight",
  bolt_parcel = "GoBolt Parcel",
  freight = "3rd Party Freight",
  untracked_shipment = "Untracked Shipment",
}

const ShipmentIndexPage: React.FC = () => {
  const organizationDetails = useOrganizationContext();
  const isFirstMountRef = useRef(true);
  const { organization_id: organizationID } = useParams<{ organization_id?: string }>();
  const [isShowFilters, setIsShowFilters] = useState(false);
  const [searchedOrganization, setSearchedOrganization] = useState("");
  const [searchedShipment, setSearchedShipment] = useUrlState({
    name: "search",
    initialValue: "",
  });
  const [selectedStatus, setSelectedStatus] = useUrlState({
    name: "status",
  });
  const [dateFromTo, setDateFromTo] = useUrlState({
    name: "date_from_to",
    initialValue: [],
  });
  const [page, setPage] = useUrlState({ name: "page", initialValue: 1 });
  const [pageSize, setPageSize] = useUrlState({
    name: "page_size",
    initialValue: 10,
  });
  const [appointmentTypesFilter, setAppointmentTypesFilter] = useUrlState({
    name: "appointmentTypes",
    initialValue: [],
  });
  const [shippingMethodsFilter, setShippingMethodsFilter] = useUrlState({
    name: "shippingMethods",
    initialValue: [],
  });

  const [organizations, setOrganizations] = useUrlState({
    name: "organizations",
  });

  const queryOptions = useMemo(() => {
    const [dateFrom, dateTo] = dateFromTo;
    const queryOption: ShipmentIndexQuery = { per_page: pageSize };
    if (organizationID) queryOption.organization_id = organizationID;
    if (page) queryOption.page = page;
    if (selectedStatus) queryOption.status = selectedStatus;
    if (dateTo) queryOption.date_to = dateTo;
    if (dateFrom) queryOption.date_from = dateFrom;
    if (searchedShipment) queryOption.external_search = searchedShipment.trim();
    if (appointmentTypesFilter.length > 0) queryOption.shipment_item_actions = appointmentTypesFilter;
    if (shippingMethodsFilter.length > 0) {
      const isFreight = shippingMethodsFilter.includes("freight");
      const isExternalCarrierParcel = shippingMethodsFilter.includes("external_carrier_shipment");

      const isBoltParcel = shippingMethodsFilter.includes("bolt_parcel");
      const isLastMileHeavy = shippingMethodsFilter.includes("last_mile_heavy");
      const isAppointment = isBoltParcel || isLastMileHeavy;

      const isUntracked = shippingMethodsFilter.includes("untracked_shipment");

      const selectedMethods = [];
      const deliveryType = [] as DeliveryType[];

      if (isExternalCarrierParcel) selectedMethods.push("external_carrier_shipment");
      if (isAppointment) selectedMethods.push("appointment");
      if (isUntracked) selectedMethods.push("untracked_shipment");

      if (isFreight && !isExternalCarrierParcel) queryOption.freight = true;
      if (!isFreight && isExternalCarrierParcel) queryOption.freight = false;

      if (isBoltParcel) deliveryType.push("parcel");
      if (isLastMileHeavy) deliveryType.push("heavy");

      queryOption.shipping_methods = selectedMethods;
      queryOption.delivery_types = deliveryType;
    }
    if (searchedOrganization?.length > 0) queryOption.organizations = organizations;
    queryOption.with_organization = true;
    queryOption.with_external_order = true;
    queryOption.with_shipping_labels = true;

    return queryOption;
  }, [
    dateFromTo,
    pageSize,
    page,
    organizationID,
    selectedStatus,
    appointmentTypesFilter,
    shippingMethodsFilter,
    searchedOrganization,
    searchedShipment,
    organizations,
  ]);

  const shipmentIndex = useQuery(["shipmentIndex", queryOptions], () => fetchShipmentIndex(queryOptions), {
    keepPreviousData: true,
  });

  const organizationIndex = useQuery(
    ["organizationIndex", searchedOrganization],
    () => fetchOrganizationIndex({ q: searchedOrganization }),
    {
      enabled: searchedOrganization.length >= 3 && !organizationID,
    }
  );

  useEffect(() => {
    setPage(isFirstMountRef.current ? page : 1);
    isFirstMountRef.current = false;
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [
    dateFromTo.length,
    pageSize,
    selectedStatus,
    searchedOrganization,
    JSON.stringify(appointmentTypesFilter),
    JSON.stringify(shippingMethodsFilter),
  ]);

  const loading = shipmentIndex.isLoading || organizationDetails.loading;

  const renderShipmentTypesFilter = () => {
    const options = Object.entries(ShipmentType).map(([key, label]) => {
      return {
        key,
        label,
        value: key,
      };
    });

    return (
      <div className="filter">
        <div className="label">Shipment Type:</div>
        <CheckboxDropdown
          options={options}
          selectedOptionKeys={appointmentTypesFilter}
          setSelectedOptionKeys={setAppointmentTypesFilter}
        />
      </div>
    );
  };

  const renderShipmentMethodFilter = () => {
    const options = Object.entries(ShipmentMethod).map(([key, label]) => {
      return {
        key,
        label,
        value: key,
      };
    });

    return (
      <div className="filter">
        <div className="label">Shipment Methods:</div>
        <CheckboxDropdown
          options={options}
          selectedOptionKeys={shippingMethodsFilter}
          setSelectedOptionKeys={setShippingMethodsFilter}
        />
      </div>
    );
  };

  const renderStatusFilter = () => {
    const options = [
      { label: "All", value: "all" },
      { label: "Label Required", value: "label_required" },
      { label: "Apply Label", value: "apply_label" },
      { label: "Label Generated", value: "label_generated" },
      { label: "Awaiting Carrier", value: "awaiting_carrier" },
      { label: "With Carrier", value: "with_carrier" },
      { label: "Completed", value: "completed" },
      { label: "Cancelled", value: "cancelled" },
      { label: "Failed", value: "failed" },
    ];

    return (
      <div className="filter">
        <div className="label">Shipment Status:</div>
        <Select
          value={selectedStatus || "All"}
          onChange={(v) => setSelectedStatus(v !== "all" && v)}
          style={{ minWidth: 120 }}
        >
          {options.map((opt) => {
            const { label, value } = opt;
            return (
              <Select.Option key={value} value={value}>
                {label}
              </Select.Option>
            );
          })}
        </Select>
      </div>
    );
  };

  const updateDates = (dates: any) => {
    if (!dates) return setDateFromTo([]);
    const [from, to] = dates as [Moment, Moment];
    const getDateString = (d: Moment) => d && d.format("YYYY-MM-DD");
    setDateFromTo([getDateString(from), getDateString(to)]);
  };

  const renderDatePicker = () => {
    const [dateFrom, dateTo] = dateFromTo;
    const from = dateFrom && moment(dateFrom);
    const to = dateTo && moment(dateTo);
    return (
      <div className="filter">
        <DatePicker.RangePicker value={[from, to]} format={"YYYY/MM/DD"} onChange={updateDates} />
      </div>
    );
  };

  const renderHeaderBtns = () => {
    return (
      <div className="header-btns">
        <DownloadCsvButton query={queryOptions} />
      </div>
    );
  };

  const renderPageSizeSelect = () => {
    const options = [
      { label: "15", value: 15 },
      { label: "30", value: 30 },
      { label: "60", value: 60 },
      { label: "100", value: 100 },
      { label: "800", value: 800 },
    ];

    return (
      <div className="filter">
        <div className="label">Page Size:</div>
        <Select value={pageSize} onChange={(v) => setPageSize(v)}>
          {options.map((opt) => {
            const { label, value } = opt;
            return (
              <Select.Option key={value} value={value}>
                {label}
              </Select.Option>
            );
          })}
        </Select>
      </div>
    );
  };

  const buildSearchOrganizationFilter = () => {
    if (organizationID) return;
    return (
      <SearchOrganizationInput
        mode="tags"
        style={{ width: 300 }}
        placeholder="Search Organization"
        loading={organizationIndex.isLoading}
        organizations={organizationIndex.data ?? []}
        defaultValue={organizations}
        onSearch={debounce(setSearchedOrganization, 800)}
        onChange={(values: Record<"label" | "value", string>[]) => setOrganizations(values?.map((v) => v.label))}
      />
    );
  };

  const buildSearchShipmentFilter = () => {
    return (
      <SearchInput
        style={{ width: 300 }}
        placeholder="Search for shipments"
        onSearch={(search) => setSearchedShipment(search)}
        onClear={() => setSearchedShipment("")}
      />
    );
  };

  const renderFilterToggleButton = () => (
    <div className="filter" style={{ marginLeft: "auto", padding: "0" }}>
      <Button type="text" onClick={() => setIsShowFilters(!isShowFilters)}>
        {isShowFilters ? <FilterFilled /> : <FilterOutlined />}
      </Button>
    </div>
  );

  const renderHeader = () => {
    return (
      <div className="shipments-header">
        <div className="title-wrapper">
          <Typography.Title level={4}>Shipments</Typography.Title>
          {renderHeaderBtns()}
        </div>
        <div className="shipment-filters">
          <Flex align="start" justify="space-between">
            <Space>
              {buildSearchShipmentFilter()}
              {buildSearchOrganizationFilter()}
            </Space>
            <Space>
              {renderDatePicker()}
              {renderFilterToggleButton()}
            </Space>
          </Flex>
          {isShowFilters && (
            <div className="filter-row">
              {renderStatusFilter()}
              {renderShipmentMethodFilter()}
              {renderShipmentTypesFilter()}
              {renderPageSizeSelect()}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="page-container" id="shipment-index-page">
      <Helmet title="Sierra - Fulfillment Shipments" />
      {renderHeader()}
      <ShipmentsTable
        shipments={shipmentIndex.data ?? []}
        page={page}
        isLoading={loading || shipmentIndex.isPreviousData}
        renderOrderIDLinkComponent={(displayText, orderID) => (
          <Link to={`/fulfillment/orders/${orderID}`}>{displayText}</Link>
        )}
        renderOrganizationLinkComponent={
          !organizationID
            ? (displayText, orgID) => <Link to={`/organizations/${orgID}`}>{displayText}</Link>
            : undefined
        }
        renderShipmentIDLinkComponent={(shipmentId: string, shipmentNumber: string, orderID: string) => (
          <Link to={`/fulfillment/orders/${orderID}/shipment/${shipmentId}/`}>{shipmentNumber}</Link>
        )}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        isAdmin
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={searchedShipment ? `No results found for: ${searchedShipment}` : `No Data`}
            />
          ),
        }}
      />
    </div>
  );
};

export default ShipmentIndexPage;
